import { axiosInstance } from "../../axios/axiosInstance";
import { API_BASE_URL, getDefaultHeaders } from "../../config";

// Trusted-roles portal (CP/CPS §5.2): routes/superadmin.php `trusted` prefix.
const TRUSTED_URL = `${API_BASE_URL}/trusted`;

const authHeaders = () => ({
  ...getDefaultHeaders(),
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
});

// Login failures are 401/403 too; they must not trigger the global logout redirect.
const loginOptions = () => ({
  headers: getDefaultHeaders(),
  _skipAuthRedirect: true,
});

// ---- Login: National ID + password, then an SMS code (default) -------------

export const getTrustedLoginOptions = () =>
  axiosInstance.get(`${TRUSTED_URL}/login/options`, loginOptions());

// The backend picks the code channel (SMS only for now).
export const trustedLoginPassword = (nationalId, password) =>
  axiosInstance.post(
    `${TRUSTED_URL}/login/password`,
    { national_id: nationalId, password },
    loginOptions(),
  );

export const trustedLoginOtp = (challenge, code) =>
  axiosInstance.post(`${TRUSTED_URL}/login/otp`, { challenge, code }, loginOptions());

export const trustedLoginOtpResend = (challenge) =>
  axiosInstance.post(`${TRUSTED_URL}/login/otp/resend`, { challenge }, loginOptions());

export const trustedPasswordForgot = (nationalId) =>
  axiosInstance.post(
    `${TRUSTED_URL}/password/forgot`,
    { national_id: nationalId },
    loginOptions(),
  );

// First login of an officer account: replace the temporary password (no code; proven at login).
export const trustedPasswordChange = (payload) =>
  axiosInstance.post(`${TRUSTED_URL}/password/change`, payload, loginOptions());

export const trustedPasswordReset = (payload) =>
  axiosInstance.post(`${TRUSTED_URL}/password/reset`, payload, loginOptions());

// ---- Login: Nafath, then TOTP (only when the backend method is "nafath") ----

export const trustedLoginInitiate = (nationalId) =>
  axiosInstance.post(
    `${TRUSTED_URL}/login/initiate`,
    { national_id: nationalId },
    loginOptions(),
  );

export const trustedLoginCheck = (transId, nationalId) =>
  axiosInstance.post(
    `${TRUSTED_URL}/login/check`,
    { transId, national_id: nationalId },
    loginOptions(),
  );

export const trustedLoginTotp = (challenge, code) =>
  axiosInstance.post(
    `${TRUSTED_URL}/login/totp`,
    { challenge, code },
    loginOptions(),
  );

export const getTrustedMe = () =>
  axiosInstance.get(`${TRUSTED_URL}/me`, { headers: authHeaders() });

export const trustedLogout = (token) =>
  axiosInstance.post(
    `${TRUSTED_URL}/logout`,
    {},
    {
      headers: { ...getDefaultHeaders(), Authorization: `Bearer ${token}` },
      _skipAuthRedirect: true,
    },
  );

// ---- RO1: validate applications --------------------------------------------

export const getRo1Applications = (params = {}) =>
  axiosInstance.get(`${TRUSTED_URL}/ro1/applications`, {
    headers: authHeaders(),
    params,
  });

export const getRo1Application = (uuid) =>
  axiosInstance.get(`${TRUSTED_URL}/ro1/applications/${uuid}`, {
    headers: authHeaders(),
  });

export const decideRo1Application = (uuid, payload) =>
  axiosInstance.post(
    `${TRUSTED_URL}/ro1/applications/${uuid}/decision`,
    payload,
    { headers: authHeaders() },
  );

// ---- RO2: issue certificates -----------------------------------------------

export const getRo2ReadyApplications = (params = {}) =>
  axiosInstance.get(`${TRUSTED_URL}/ro2/applications/ready`, {
    headers: authHeaders(),
    params,
  });

export const getRo2FailedApplications = (params = {}) =>
  axiosInstance.get(`${TRUSTED_URL}/ro2/applications/failed`, {
    headers: authHeaders(),
    params,
  });

export const getRo2Application = (uuid) =>
  axiosInstance.get(`${TRUSTED_URL}/ro2/applications/${uuid}`, {
    headers: authHeaders(),
  });

export const issueRo2Application = (uuid) =>
  axiosInstance.post(
    `${TRUSTED_URL}/ro2/applications/${uuid}/issue`,
    {},
    { headers: authHeaders() },
  );

export const retryRo2Application = (uuid) =>
  axiosInstance.post(
    `${TRUSTED_URL}/ro2/applications/${uuid}/retry`,
    {},
    { headers: authHeaders() },
  );

// ---- Revocations -------------------------------------------------------------

export const getRevocationQueues = (role) =>
  axiosInstance.get(`${TRUSTED_URL}/${role}/revocations`, {
    headers: authHeaders(),
  });

export const createOfficerRevocation = (payload) =>
  axiosInstance.post(`${TRUSTED_URL}/ro1/revocations`, payload, {
    headers: authHeaders(),
  });

export const reviewRevocationRo1 = (id, payload) =>
  axiosInstance.post(`${TRUSTED_URL}/ro1/revocations/${id}/review`, payload, {
    headers: authHeaders(),
  });

export const compromiseReviewRevocation = (role, id, payload) =>
  axiosInstance.post(
    `${TRUSTED_URL}/${role}/revocations/${id}/compromise-review`,
    payload,
    { headers: authHeaders() },
  );

export const confirmRevocationSo = (id, payload) =>
  axiosInstance.post(
    `${TRUSTED_URL}/security/revocations/${id}/confirm`,
    payload,
    { headers: authHeaders() },
  );

export const executeRevocationRo2 = (id) =>
  axiosInstance.post(
    `${TRUSTED_URL}/ro2/revocations/${id}/execute`,
    {},
    { headers: authHeaders() },
  );

// ---- Complaints and problem reports ----------------------------------------

export const getTickets = (params = {}) =>
  axiosInstance.get(`${TRUSTED_URL}/tickets`, {
    headers: authHeaders(),
    params,
  });

export const getTicket = (reference) =>
  axiosInstance.get(`${TRUSTED_URL}/tickets/${reference}`, {
    headers: authHeaders(),
  });

export const acknowledgeTicket = (reference, payload) =>
  axiosInstance.post(
    `${TRUSTED_URL}/tickets/${reference}/acknowledge`,
    payload,
    { headers: authHeaders() },
  );

export const decideTicket = (reference, payload) =>
  axiosInstance.post(`${TRUSTED_URL}/tickets/${reference}/decide`, payload, {
    headers: authHeaders(),
  });

export const revokeFromTicket = (reference, payload) =>
  axiosInstance.post(
    `${TRUSTED_URL}/tickets/${reference}/revocation`,
    payload,
    { headers: authHeaders() },
  );

// ---- Officer task notifications ------------------------------------------------

export const getTrustedNotifications = (params = {}) =>
  axiosInstance.get(`${TRUSTED_URL}/notifications`, { headers: authHeaders(), params });

export const getTrustedUnreadCount = () =>
  axiosInstance.get(`${TRUSTED_URL}/notifications/unread-count`, { headers: authHeaders() });

export const markTrustedNotificationRead = (id) =>
  axiosInstance.post(`${TRUSTED_URL}/notifications/${id}/read`, {}, { headers: authHeaders() });

export const markAllTrustedNotificationsRead = () =>
  axiosInstance.post(`${TRUSTED_URL}/notifications/read-all`, {}, { headers: authHeaders() });

// ---- Audit log (System Auditor, Security Officer) -----------------------------

export const getTrustedAuditLogs = (params = {}) =>
  axiosInstance.get(`${TRUSTED_URL}/audit-logs`, { headers: authHeaders(), params });
